// Phase 2 — Maintenance Module API client
// Mirrors backend/app/api/routers/maintenance.py 1:1.

import apiClient from '@/shared/api/client'
import type {
  AuditEntry,
  FailureReason,
  KPISummary,
  MachineHealthInfo,
  MaintenanceRequest,
  MaintenanceWorkOrder,
  PMSchedule,
  PMTemplate,
  ShutdownPlan,
  SparePart,
  SpareTransaction,
  UtilityLog,
} from '@/shared/types/maintenance'

type Params = Record<string, unknown>

// ── Failure reasons ─────────────────────────────────────────────────────

export const failureReasonsApi = {
  list: async (): Promise<FailureReason[]> => {
    const r = await apiClient.get<FailureReason[]>('/maintenance/failure-reasons')
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<FailureReason> => {
    const r = await apiClient.post<FailureReason>('/maintenance/failure-reasons', data)
    return r.data
  },
}

// ── Maintenance Requests ─────────────────────────────────────────────────

export const maintenanceRequestsApi = {
  list: async (params?: Params): Promise<MaintenanceRequest[]> => {
    const r = await apiClient.get<MaintenanceRequest[]>('/maintenance/requests', { params })
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<MaintenanceRequest> => {
    const r = await apiClient.post<MaintenanceRequest>('/maintenance/requests', data)
    return r.data
  },
  promote: async (
    requestId: string,
    body: { maintenance_type?: string; assigned_to?: string | null } = {},
  ): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(
      `/maintenance/requests/${requestId}/promote`,
      { maintenance_type: 'BREAKDOWN', ...body },
    )
    return r.data
  },
}

// ── Work Orders ──────────────────────────────────────────────────────────

export const workOrdersApi = {
  list: async (params?: Params): Promise<MaintenanceWorkOrder[]> => {
    const r = await apiClient.get<MaintenanceWorkOrder[]>('/maintenance/work-orders', { params })
    return r.data
  },
  get: async (id: string): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.get<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}`)
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>('/maintenance/work-orders', data)
    return r.data
  },
  assign: async (
    id: string, body: { assigned_to: string; planned_start?: string | null },
  ): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}/assign`, body)
    return r.data
  },
  start: async (id: string): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}/start`)
    return r.data
  },
  waitForSpares: async (id: string): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}/waiting-for-spares`)
    return r.data
  },
  complete: async (
    id: string,
    body: {
      root_cause_id?: string | null
      action_taken?: string | null
      closure_remarks?: string | null
    },
  ): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}/complete`, body)
    return r.data
  },
  close: async (
    id: string, body: { closure_remarks?: string | null } = {},
  ): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}/close`, body)
    return r.data
  },
  reopen: async (id: string, body: { reason: string }): Promise<MaintenanceWorkOrder> => {
    const r = await apiClient.post<MaintenanceWorkOrder>(`/maintenance/work-orders/${id}/reopen`, body)
    return r.data
  },
}

// ── PM Templates + Schedules ────────────────────────────────────────────

export const pmTemplatesApi = {
  list: async (params?: Params): Promise<PMTemplate[]> => {
    const r = await apiClient.get<PMTemplate[]>('/maintenance/pm-templates', { params })
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<PMTemplate> => {
    const r = await apiClient.post<PMTemplate>('/maintenance/pm-templates', data)
    return r.data
  },
}

export const pmSchedulesApi = {
  list: async (params?: Params): Promise<PMSchedule[]> => {
    const r = await apiClient.get<PMSchedule[]>('/maintenance/pm-schedules', { params })
    return r.data
  },
  generate: async (body: {
    plant_id: string
    from_date: string
    to_date: string
    machine_ids?: string[]
    frequencies?: string[]
  }): Promise<{ created: number; skipped: number }> => {
    const r = await apiClient.post('/maintenance/pm-schedules/generate', body)
    return r.data
  },
  markOverdue: async (plant_id?: string): Promise<{ flipped_to_overdue: number }> => {
    const r = await apiClient.post('/maintenance/pm-schedules/mark-overdue', null, {
      params: plant_id ? { plant_id } : undefined,
    })
    return r.data
  },
  complete: async (
    scheduleId: string,
    body: {
      checklist_results_json: Record<string, unknown>
      abnormality_found?: boolean
      follow_up_action?: string | null
      remarks?: string | null
    },
  ): Promise<PMSchedule> => {
    const r = await apiClient.post<PMSchedule>(
      `/maintenance/pm-schedules/${scheduleId}/complete`, body,
    )
    return r.data
  },
  defer: async (
    scheduleId: string,
    body: { reason: string; new_date: string },
  ): Promise<PMSchedule> => {
    const r = await apiClient.post<PMSchedule>(
      `/maintenance/pm-schedules/${scheduleId}/defer`, body,
    )
    return r.data
  },
}

// ── Spares ──────────────────────────────────────────────────────────────

export const sparesApi = {
  list: async (params?: Params): Promise<SparePart[]> => {
    const r = await apiClient.get<SparePart[]>('/maintenance/spares', { params })
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<SparePart> => {
    const r = await apiClient.post<SparePart>('/maintenance/spares', data)
    return r.data
  },
  bulkImport: async (rows: Record<string, unknown>[]): Promise<{
    total: number; created: number; updated: number;
    errors: Array<{ row_index: number; error: string }>
  }> => {
    const r = await apiClient.post('/maintenance/spares/bulk-import', { rows })
    return r.data
  },
  issue: async (body: {
    spare_id: string
    work_order_id: string
    issued_qty: number
    issued_to?: string | null
    remarks?: string | null
  }): Promise<SpareTransaction> => {
    const r = await apiClient.post<SpareTransaction>('/maintenance/spares/issue', body)
    return r.data
  },
  return: async (body: {
    transaction_id: string
    returned_qty: number
    remarks?: string | null
  }): Promise<SpareTransaction> => {
    const r = await apiClient.post<SpareTransaction>('/maintenance/spares/return', body)
    return r.data
  },
}

// ── KPIs / Health / History ─────────────────────────────────────────────

export const maintenanceKpiApi = {
  summary: async (params: {
    plant_id: string; from_date?: string; to_date?: string;
  }): Promise<KPISummary> => {
    const r = await apiClient.get<KPISummary>('/maintenance/kpi-summary', { params })
    return r.data
  },
  repeatBreakdown: async (params: {
    plant_id: string; days?: number; min_occurrences?: number;
  }): Promise<Array<{ machine_id: string; occurrences: number }>> => {
    const r = await apiClient.get('/maintenance/repeat-breakdown', { params })
    return r.data
  },
  machineHistory: async (machineId: string, limit = 100): Promise<{
    machine_id: string
    health_score: number
    health_status: string
    open_breakdowns: number
    open_work_orders: number
    last_pm_at: string | null
    next_pm_at: string | null
    repeat_breakdown_count: number
    work_orders: Array<Record<string, unknown>>
    pm_schedules: Array<Record<string, unknown>>
  }> => {
    const r = await apiClient.get(`/maintenance/machines/${machineId}/history`, { params: { limit } })
    return r.data
  },
  recomputeHealth: async (machineId: string): Promise<MachineHealthInfo> => {
    const r = await apiClient.post<MachineHealthInfo>(`/maintenance/machines/${machineId}/recompute-health`)
    return r.data
  },
}

// ── Shutdown Plans ──────────────────────────────────────────────────────

export const shutdownPlansApi = {
  list: async (params?: Params): Promise<ShutdownPlan[]> => {
    const r = await apiClient.get<ShutdownPlan[]>('/maintenance/shutdown-plans', { params })
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<ShutdownPlan> => {
    const r = await apiClient.post<ShutdownPlan>('/maintenance/shutdown-plans', data)
    return r.data
  },
  approve: async (
    id: string,
    body: { role: 'production' | 'plant_head'; approved: boolean; remarks?: string },
  ): Promise<ShutdownPlan> => {
    const r = await apiClient.post<ShutdownPlan>(
      `/maintenance/shutdown-plans/${id}/approve`, body,
    )
    return r.data
  },
}

// ── Utility Logs ────────────────────────────────────────────────────────

export const utilityLogsApi = {
  list: async (params?: Params): Promise<UtilityLog[]> => {
    const r = await apiClient.get<UtilityLog[]>('/maintenance/utility-logs', { params })
    return r.data
  },
  create: async (data: Record<string, unknown>): Promise<UtilityLog> => {
    const r = await apiClient.post<UtilityLog>('/maintenance/utility-logs', data)
    return r.data
  },
}

// ── Audit Trail ─────────────────────────────────────────────────────────

export const maintenanceAuditApi = {
  forEntity: async (entityId: string, limit = 100): Promise<AuditEntry[]> => {
    const r = await apiClient.get<AuditEntry[]>(`/maintenance/audit/${entityId}`, { params: { limit } })
    return r.data
  },
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 3 — Dashboards & reports
// ═══════════════════════════════════════════════════════════════════════════

export interface ReportFilters {
  plant_id: string
  from_date?: string
  to_date?: string
  department_id?: string
  machine_id?: string
  machine_type?: string
  frequency?: string
  skill_type?: string
  maintenance_type?: string
  critical_only?: boolean
  abnormal_only?: boolean
  waiting_spare_only?: boolean
  bucket?: string
  priority?: string
  utility_type?: string
  band?: string
  min_occurrences?: number
}

function trimEmpty(p: ReportFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    out[k] = v
  }
  return out
}

export const maintenanceDashboardApi = {
  manager: async (params: ReportFilters): Promise<Record<string, unknown>> => {
    const r = await apiClient.get('/maintenance/dashboard/manager', { params: trimEmpty(params) })
    return r.data
  },
  plantHead: async (params: ReportFilters): Promise<Record<string, unknown>> => {
    const r = await apiClient.get('/maintenance/dashboard/plant-head', { params: trimEmpty(params) })
    return r.data
  },
}

export const maintenanceReportsApi = {
  machineDowntime: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/machine-downtime', { params: trimEmpty(params) })
    return r.data
  },
  departmentBreakdown: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/department-breakdown', { params: trimEmpty(params) })
    return r.data
  },
  pmCompliance: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/pm-compliance', { params: trimEmpty(params) })
    return r.data
  },
  spareConsumption: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/spare-consumption', { params: trimEmpty(params) })
    return r.data
  },
  technicianPerformance: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/technician-performance', { params: trimEmpty(params) })
    return r.data
  },
  cost: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/cost', { params: trimEmpty(params) })
    return r.data
  },
  repeatBreakdown: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/repeat-breakdown', { params: trimEmpty(params) })
    return r.data
  },
  pendingAgeing: async (params: ReportFilters): Promise<{
    rows: Array<Record<string, unknown>>
    bucket_distribution: Record<string, number>
  }> => {
    const r = await apiClient.get('/maintenance/reports/pending-ageing', { params: trimEmpty(params) })
    return r.data
  },
  utilityPerformance: async (params: ReportFilters): Promise<{
    rows: Array<Record<string, unknown>>
    by_utility: Array<Record<string, unknown>>
  }> => {
    const r = await apiClient.get('/maintenance/reports/utility-performance', { params: trimEmpty(params) })
    return r.data
  },
  machineHealth: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/machine-health', { params: trimEmpty(params) })
    return r.data
  },
  // Phase 3.1 — Cost per kg
  costPerKgByMachine: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/cost-per-kg/by-machine', { params: trimEmpty(params) })
    return r.data
  },
  costPerKgByDepartment: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/cost-per-kg/by-department', { params: trimEmpty(params) })
    return r.data
  },
  costPerKgByCount: async (params: ReportFilters): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/cost-per-kg/by-count', { params: trimEmpty(params) })
    return r.data
  },
  costPerKgMonthlyTrend: async (params: { plant_id: string; months?: number }): Promise<Array<Record<string, unknown>>> => {
    const r = await apiClient.get('/maintenance/reports/cost-per-kg/monthly-trend', { params })
    return r.data
  },
  workOrderDowntimeCost: async (woId: string): Promise<{
    value: number | null
    source: string
    standard_missing: boolean
    standard_kg_per_hour?: number
    downtime_hours?: number
    contribution_per_kg?: number
  }> => {
    const r = await apiClient.get(`/maintenance/work-orders/${woId}/downtime-cost`)
    return r.data
  },
}

/**
 * Build the absolute URL for a CSV export hitting the backend. We rely on
 * the auth token attached by the apiClient interceptor — for download links
 * we fall through to a fetch with the same header so the user doesn't see a
 * 401 popup when the token is missing.
 */
export async function downloadMaintenanceReportCsv(
  reportPath: string, params: ReportFilters, filename: string,
): Promise<void> {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(trimEmpty(params))) {
    search.set(k, String(v))
  }
  search.set('format', 'csv')
  const url = `/api/v1${reportPath}?${search.toString()}`
  const raw = localStorage.getItem('auth-storage')
  let token: string | undefined
  if (raw) {
    try { token = JSON.parse(raw)?.state?.accessToken } catch { /* ignore */ }
  }
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) {
    throw new Error(`Export failed (${resp.status})`)
  }
  const blob = await resp.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}
